import { InvalidateQueryFilters, QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * Checks if any queries matching the given query key are stale compared to a provided timestamp.
 *
 * This utility is used to determine if queries should be invalidated or updated after a job or process
 * has finished, by comparing the last data update time of each query to the job's finished time.
 *
 * @returns A function that takes a finishedAt date and a queryKey, and returns true if any matching queries are stale.
 */

const hasAnyQueryStale = (queryClient: QueryClient) => {
  return (finishedAt: Date, queryKey: QueryKey) => {
    const queries = queryClient.getQueryCache().findAll({
      queryKey,
      exact: false,
    });

    return queries.some((query) => {
      const updatedAt = query.state.dataUpdatedAt;
      return updatedAt && updatedAt < finishedAt.getTime();
    });
  };
};

export const invalidateIfOutdated = async (
  queryClient: QueryClient,
  finishedAt: Date,
  queryKey: QueryKey,
  filters?: InvalidateQueryFilters
): Promise<void> => {
  const isAnyQueryStale = hasAnyQueryStale(queryClient);

  if (!isAnyQueryStale(finishedAt, queryKey)) return;

  queryClient.invalidateQueries({
    queryKey,
    refetchType: "all",
    ...filters,
  });
};

export const updateIfOutdated = async (
  queryClient: QueryClient,
  finishedAt: Date,
  queryKey: QueryKey,
  updater: () => Promise<void>
): Promise<void> => {
  const isAnyQueryStale = hasAnyQueryStale(queryClient);

  if (!isAnyQueryStale(finishedAt, queryKey)) return;

  await updater();
};
